'use client'

import React, { useEffect, useRef, useState } from 'react'
import type { BenefitsGridBlock as BenefitsGridProps, Media } from '@/payload-types'
import RichText from '@/components/RichText'
import { bgClass } from '@/utilities/bgClass'

function useInView<T extends HTMLElement>(threshold = 0.15) {
  const ref = useRef<T | null>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    if (!ref.current) return
    const el = ref.current
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true)
            obs.unobserve(e.target)
          }
        })
      },
      { threshold },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])

  return { ref, inView }
}

/* ------------------- card ------------------- */
type Benefit = NonNullable<BenefitsGridProps['benefits']>[number]

function BenefitCard({ item, index }: { item: Benefit; index: number }) {
  const { ref, inView } = useInView<HTMLDivElement>(0.18)
  const img = (item?.image as Media) || undefined

  return (
    <div
      ref={ref}
      className={`
        relative overflow-hidden rounded-2xl
        bg-mini-dartext-mini-dark-secondary/40 dark:bg-neutral-800/40
        transition
        aspect-square w-full md:max-h-[400px] md:max-w-[400px] md:mx-auto
        transform-gpu
        ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
      `}
      style={{ transitionDuration: '1500ms', transitionDelay: `${index * 180}ms` }}
    >
      {/* full-bleed image */}
      {img?.url ? (
        <img
          src={img.url}
          alt={img.alt || item.title}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-neutral-300 dark:bg-neutral-700" />
      )}

      {/* bottom tray — square corners; parent clips the radius */}
      <div className="absolute inset-x-0 bottom-0 will-change-[filter]">
        <div className="bg-mini-white/85 dark:bg-mini-black/80 backdrop-blur-md p-4 h-44">
          <h3 className="font-medium text-xl leading-snug text-mini-primary dark:text-mini-dark-primary">
            {item.title}
          </h3>
          <p className="mt-2 text-base leading-relaxed text-mini-secondary dark:text-mini-dark-secondary line-clamp-4 overflow-hidden">
            {item.description}
          </p>
        </div>
      </div>
    </div>
  )
}

export const BenefitsGridBlock = (props: BenefitsGridProps & { id?: string }) => {
  const { id, introContent, benefits = [], background = 'none' } = props

  return (
    <section id={id ? `block-${id}` : undefined} className={`${bgClass(background)} py-12 md:pb-20`}>
      {/* header */}
      {introContent ? (
        <div className="container mb-10 md:mb-12">
          <RichText
            className="
              ms-0 
              [&_p]:max-w-3xl
            "
            data={introContent}
            enableGutter={false}
          />
        </div>
      ) : null}

      {/* grid */}
      <div className="container">
        <div className="grid justify-items-stretch lg:justify-items-center gap-6 sm:gap-7 md:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item: Benefit, i: number) => (
            <BenefitCard key={item?.id ?? i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default BenefitsGridBlock
